import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateContractInput, UpdateContractInput } from '@/lib/marina/validators'

export type ContractStatus = 'DRAFT' | 'PREPARED' | 'PENDING_REVIEW' | 'ACTIVE' | 'EXPIRED' | 'TERMINATED'

export type ContractType = 'SEASONAL' | 'ANNUAL' | 'MONTHLY' | 'WEEKLY'

export interface ContractItem {
  id: string
  description: string
  quantity: string
  unitPriceIdr: string
  amountIdr: string
  sortOrder: number
}

export interface Contract {
  id: string
  code: string
  customerId: string
  customer?: { id: string; firstName: string; lastName: string; category: string }
  boatId: string | null
  boat?: { id: string; name: string; type?: string } | null
  berthId: string | null
  berth?: { id: string; code: string } | null
  type: ContractType
  startDate: string
  endDate: string
  totalValueIdr: string
  depositIdr: string | null
  currency: string
  fxRateToBase: string | null
  documentUrl: string | null
  questions: { q: string; a?: string }[] | null
  notes: string | null
  status: ContractStatus
  items?: ContractItem[]
  createdAt: string
  updatedAt: string
}

export interface ContractsListResponse {
  items: Contract[]
  total: number
  page: number
  pageSize: number
}

export interface ContractsListParams {
  q?: string
  status?: ContractStatus
  customerId?: string
  boatId?: string
  page?: number
  pageSize?: number
  sort?: string
  order?: 'asc' | 'desc'
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function toQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function useContracts(params: ContractsListParams = {}) {
  return useQuery({
    queryKey: ['contracts', params],
    queryFn: () => apiFetch<ContractsListResponse>(`/api/contracts${toQuery(params as Record<string, unknown>)}`),
    staleTime: 15_000,
  })
}

export function useContractStatusCounts() {
  return useQuery({
    queryKey: ['contracts', 'status-counts'],
    queryFn: () => apiFetch<{ counts: Record<string, number> }>('/api/contracts/status-counts'),
    staleTime: 15_000,
  })
}

export function useContract(id: string | null | undefined) {
  return useQuery({
    queryKey: ['contract', id],
    queryFn: () => apiFetch<{ contract: Contract }>(`/api/contracts/${id}`),
    enabled: !!id,
  })
}

export function useCreateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateContractInput) =>
      apiFetch<{ contract: Contract }>('/api/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  })
}

export function useUpdateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContractInput }) =>
      apiFetch<{ contract: Contract }>(`/api/contracts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['contracts'] })
      qc.invalidateQueries({ queryKey: ['contract', vars.id] })
    },
  })
}

export function useDeleteContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/api/contracts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts'] }),
  })
}

function transition(action: 'mark-prepared' | 'submit-review' | 'activate' | 'terminate') {
  return (id: string) => apiFetch<{ contract: Contract }>(`/api/contracts/${id}/${action}`, { method: 'POST' })
}

export function useMarkPrepared() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: transition('mark-prepared'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['contracts'] })
      qc.invalidateQueries({ queryKey: ['contract', id] })
    },
  })
}
export function useSubmitReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: transition('submit-review'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['contracts'] })
      qc.invalidateQueries({ queryKey: ['contract', id] })
    },
  })
}
export function useActivateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: transition('activate'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['contracts'] })
      qc.invalidateQueries({ queryKey: ['contract', id] })
    },
  })
}
export function useTerminateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: transition('terminate'),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['contracts'] })
      qc.invalidateQueries({ queryKey: ['contract', id] })
    },
  })
}
